import { ReactNode, createContext, useContext, useState } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import { Separator } from '../../../components/ui/separator';
import { cn } from '../../../lib/utils';
import { BulkActionButton } from './BulkActionButton';

interface BulkActionsBarProps {
  /** Number of selected items */
  selectedCount: number;
  /** Callback to clear selection */
  onClearSelection: () => void;
  /** Bulk action buttons/elements */
  children?: ReactNode;
  /**
   * Declared actions, rendered before `children`.
   *
   * The declarative half of the bar. Use it for anything a registry or a shared
   * component produces — a bulk entity link, a status set, an assignment — and
   * keep `children` for the hand-written one-offs a call site owns. Both render
   * inside the same scope context, so the escalation policy is identical either
   * way.
   *
   * **A destructive action that is also `escalatable` belongs in `children`**,
   * not here: it must route through a sibling `BulkScopeConfirmDialog` naming
   * the count and the rows the user cannot see (`ui-list-pages.md` §11), and a
   * declared action has nowhere to mount one.
   */
  actions?: BulkAction[];
  /** Custom label override. If not provided, uses generic "X items selected" */
  label?: string;
  /**
   * What the selection actually covers. **Every server-paged list MUST pass
   * `"page"`** (ui-list-pages.md §11 → Select-all on a paged list).
   *
   * On a client-sliced list the browser holds the whole set, so the header
   * checkbox really does select everything and the plain "X items selected"
   * label is true. On a server-paged list the same checkbox silently selects
   * only the rows currently loaded — and nothing on screen says so, which is
   * how a user comes to believe they voided 340 invoices when they voided 20.
   * Passing `"page"` puts the scope in the label instead of leaving the user
   * to infer it.
   *
   * Omit it only where the component genuinely holds the full set.
   *
   * `"matching"` is the ESCALATED state: the user has explicitly asked to act on
   * every row matching the current filters, not just the loaded page. Never set
   * it as an initial value — it is reached only by the user pressing the
   * escalation, because "select all" must never silently change meaning.
   */
  selectionScope?: 'page' | 'matching';
  /**
   * Total rows matching the current filters (the list's `total`, not the page).
   * When it exceeds the selection, the label names both numbers so the gap
   * between "selected" and "matching" is visible rather than implied.
   */
  matchingCount?: number;
  /**
   * Escalate the selection to every row matching the current filters.
   *
   * **Passing this handler is what enables the escalation** — there is no
   * `allowSelectAllMatching` flag, so a visible "Select all N matching" link can
   * never be backed by nothing (the same rule the pickers' inline-create row
   * follows). Omit it and the bar stays page-scoped.
   *
   * Only pass it on a list whose bulk endpoint accepts the filter set
   * (`shared/utils/bulk_scope.py`). A list whose only bulk actions are
   * destructive should not pass it at all.
   */
  onSelectAllMatching?: () => void;
  /** Return an escalated selection to the loaded page. Required alongside
   * `onSelectAllMatching` — an escalation the user cannot back out of is a trap. */
  onSelectPageOnly?: () => void;
  /** Additional className */
  className?: string;
}

/**
 * The selection scope, published to the bar's action buttons.
 *
 * `BulkActionButton` reads this to refuse a destructive action while the
 * selection is escalated. It is context rather than a prop so the policy cannot
 * be forgotten by an author wiring up a new action — see
 * `docs/design-system/ui-list-pages.md` §11.
 */
export const BulkSelectionScopeContext = createContext<'page' | 'matching' | 'set'>('set');

export function useBulkSelectionScope() {
  return useContext(BulkSelectionScopeContext);
}

/** What a bar needs to describe its selection honestly. */
export interface BulkScopeLabelInput {
  /** Explicit override. Always wins — a caller that knows better than the scope. */
  label?: string;
  /** Rows the browser currently holds ticked. */
  selectedCount: number;
  selectionScope?: 'page' | 'matching';
  /** Total rows matching the current filters (the list's `total`). */
  matchingCount?: number;
}

/**
 * The one place "N selected" is turned into a sentence.
 *
 * Shared by all three bulk bars. It exists because the wording is the *contract*
 * — "20 items selected" over a 500-row filtered set is the defect §11 is about —
 * and a second copy of this ternary is a second place for that contract to rot.
 * `InlineBulkActionsBar` and `FloatingBulkActionsBar` shipped without it and read
 * "20 items selected" on every server-paged host they had.
 */
export function useBulkScopeLabel({
  label,
  selectedCount,
  selectionScope,
  matchingCount,
}: BulkScopeLabelInput): string {
  const { t } = useTranslation('common');

  const escalated = selectionScope === 'matching';
  // An escalated selection covers the whole matched set, so `selectedCount` (the
  // loaded page) is no longer what the user is acting on — the matching count is.
  const effectiveCount = escalated && matchingCount !== undefined ? matchingCount : selectedCount;

  // Explicit label always wins; otherwise the scope decides how honest we can be.
  return (
    label ??
    (escalated
      ? t('app.allMatchingSelected', { count: effectiveCount })
      : selectionScope !== 'page'
        ? t('app.itemsSelected', { count: selectedCount })
        : matchingCount !== undefined && matchingCount > selectedCount
          ? t('app.itemsSelectedOnPageOfMatching', { count: selectedCount, matching: matchingCount })
          : t('app.itemsSelectedOnPage', { count: selectedCount }))
  );
}

/* -------------------------------------------------------------------------
 * The declarative action vocabulary
 *
 * These types were `FloatingBulkActionsBar`'s, with one consumer. They live
 * HERE now because they describe what a bulk action IS, not how one particular
 * bar draws it — and because `BulkActionsBar` was layout-only, `children:
 * ReactNode` being its entire vocabulary, so every action at all 17 call sites
 * is hand-written JSX. A declared action can be produced by a registry; a
 * `ReactNode` can only be typed out.
 *
 * The `picker` variant is why the promotion happened
 * (`docs/platform/entity-relationship-surfaces-design.md` §6.4): "pick a target,
 * then apply to the selection" is the shape EVERY bulk relationship edit takes,
 * so once it is a declaration the registry can emit one per relationship and
 * nobody writes a bulk-link feature again.
 * ------------------------------------------------------------------------- */

/** Item inside a submenu page (e.g. a status option, a user, a label). */
export interface BulkSubItem {
  id: string;
  label: string;
  /** Icon/swatch/badge rendered before the label inside the command item. */
  leading?: ReactNode;
  /** Extra content after the label (e.g. an "Active" tag). */
  trailing?: ReactNode;
  onSelect: () => void | Promise<void>;
}

export interface BulkActionBase {
  id: string;
  label: string;
  icon?: LucideIcon;
  /**
   * This action cannot be undone from the UI — delete, archive, void, reject.
   *
   * It carries the destructive styling AND, together with `escalatable` below,
   * the same scope policy `BulkActionButton` applies. A red tint on its own was
   * what this flag used to mean, and that is exactly how a Delete came to sit in
   * a bar reading "20 items selected" over a 500-row server-paged set
   * (`docs/design-system/ui-list-pages.md` §11).
   */
  destructive?: boolean;
  /**
   * This action's endpoint accepts the list's **filter set**, so it can act on
   * every matching row rather than only the loaded page.
   *
   * Opt-in: the capability is a fact about a backend request model that no
   * frontend check can derive, so an undeclared action is refused while the
   * selection is escalated rather than quietly acting on one page of it.
   */
  escalatable?: boolean;
  /**
   * Why this action cannot reach every matching row — already translated, and
   * shown on screen rather than only on hover. Use it for a constraint inherent
   * to the action; omit it when the endpoint is merely awaiting a filter-set arm
   * and take the generic reason.
   */
  cannotEscalate?: string;
  disabled?: boolean;
}

/** An action that runs immediately when selected. */
export interface BulkLeafAction extends BulkActionBase {
  kind: 'leaf';
  onSelect: () => void | Promise<void>;
}

/** An action that opens a submenu page with its own item list. */
export interface BulkSubmenuAction extends BulkActionBase {
  kind: 'submenu';
  /** Heading shown above the submenu's CommandGroup. Defaults to `label`. */
  heading?: string;
  items: BulkSubItem[];
}

/**
 * An action that opens a picker, then applies the chosen target to the selection.
 *
 * The bar owns the open/closed state and nothing else. `renderPicker` is a
 * render prop rather than a picker CONFIG because the picker's query, filter
 * controls and columns belong to the caller by contract (§6.2: "reuses the
 * target list page's filter set, columns and sort" means literally reusing
 * them). A shared component that re-derived them would be a second copy of the
 * target list page to keep in sync — so the declaration says *that a target is
 * picked*, and the owning module says *how*.
 */
export interface BulkPickerAction extends BulkActionBase {
  kind: 'picker';
  /**
   * Render the picker. `onOpenChange(false)` closes it; the picker calls
   * whatever it likes on confirm — applying the selection is the caller's job,
   * because only the caller knows which endpoint and which payload.
   */
  renderPicker: (props: { open: boolean; onOpenChange: (open: boolean) => void }) => ReactNode;
}

export type BulkAction = BulkLeafAction | BulkSubmenuAction | BulkPickerAction;

/**
 * The subset a COMMAND-PALETTE bar can draw.
 *
 * `FloatingBulkActionsBar` and `InlineBulkActionsBar` render their actions
 * inside a command dialog or a dropdown, where there is nowhere to mount a
 * modal picker — opening one from inside a `CommandDialog` puts two competing
 * overlays on screen. They therefore accept this narrower union rather than
 * silently ignoring a `picker` action they cannot draw, which is the failure a
 * shared type is supposed to prevent.
 */
export type BulkCommandAction = BulkLeafAction | BulkSubmenuAction;

/**
 * Whether the bar must refuse this action for the selection it is describing.
 *
 * The same predicate `BulkActionButton` applies, factored out so the bars that
 * render their actions themselves (rather than taking them as children) cannot
 * drift from it.
 */
export function isBlockedByScope(
  action: BulkAction,
  selectionScope: 'page' | 'matching' | undefined,
): boolean {
  return selectionScope === 'matching' && !action.escalatable;
}

/**
 * A bar that appears when items are selected, showing selection count and bulk actions.
 * Use this with DataTable's selectable feature for multi-select operations.
 *
 * Styled consistently with the Tasks module's BulkActionToolbar:
 * dark background, count + clear button, separator, then action buttons.
 *
 * On a server-paged list, pass `selectionScope="page"` (plus the list's filtered
 * `total` as `matchingCount`) so the bar says what the selection really covers.
 * If its bulk endpoint accepts the filter set (`shared/utils/bulk_scope.py`),
 * also pass `onSelectAllMatching` / `onSelectPageOnly` to offer the escalation.
 *
 * Actions are `BulkActionButton`s, not raw `Button`s: that component carries the
 * on-primary styling, and every action under an escalating bar states whether it
 * can reach every matching row — `escalatable` when its endpoint takes the filter
 * set, nothing when it does not (it is then refused, visibly, while escalated).
 * A destructive action that IS escalatable confirms through
 * `BulkScopeConfirmDialog`. All three are enforced by
 * `tools/check_bulk_matching_policy.py`.
 *
 * @example
 * ```tsx
 * {selectedIds.length > 0 && (
 *   <BulkActionsBar
 *     selectedCount={selectedIds.length}
 *     selectionScope={scope}
 *     matchingCount={totalCount}
 *     onSelectAllMatching={() => setScope('matching')}
 *     onSelectPageOnly={() => setScope('page')}
 *     onClearSelection={() => setSelectedIds([])}
 *   >
 *     <BulkActionButton escalatable onClick={handleBulkResolve}>
 *       <CheckCircle className="h-4 w-4 me-2" />
 *       {t('bulkResolve')}
 *     </BulkActionButton>
 *     <BulkActionButton escalatable destructive onClick={() => setDeleteOpen(true)}>
 *       {t('common:app.delete')}
 *     </BulkActionButton>
 *   </BulkActionsBar>
 * )}
 * ```
 */
/**
 * One declared action, drawn as the same `BulkActionButton` a hand-written child
 * would be — so the scope policy, the on-primary styling and the visible refusal
 * reason are shared rather than reimplemented for declared actions.
 */
/* bulk-matching-ok: this renderer FORWARDS an action's declared flags; it
   declares none of its own. The confirmation contract binds whoever writes the
   action, and a declared action has nowhere to mount a BulkScopeConfirmDialog —
   so a destructive escalatable action belongs in `children`, beside its dialog
   (stated on the `actions` prop above). The gate reads JSX attributes and cannot
   follow a forwarded prop to its declaration site. */
function DeclaredBulkAction({ action }: { action: BulkAction }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const Icon = action.icon;
  const content = (
    <>
      {Icon && <Icon className="h-4 w-4 me-2" />}
      {action.label}
    </>
  );

  if (action.kind === 'submenu') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <BulkActionButton
            destructive={action.destructive}
            escalatable={action.escalatable}
            cannotEscalate={action.cannotEscalate}
            disabled={action.disabled}
          >
            {content}
          </BulkActionButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{action.heading ?? action.label}</DropdownMenuLabel>
          {action.items.map((item) => (
            <DropdownMenuItem key={item.id} onSelect={() => void item.onSelect()}>
              {item.leading}
              <span className="flex-1">{item.label}</span>
              {item.trailing}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (action.kind === 'picker') {
    return (
      <>
        <BulkActionButton
          destructive={action.destructive}
          escalatable={action.escalatable}
          cannotEscalate={action.cannotEscalate}
          disabled={action.disabled}
          onClick={() => setPickerOpen(true)}
        >
          {content}
        </BulkActionButton>
        {action.renderPicker({ open: pickerOpen, onOpenChange: setPickerOpen })}
      </>
    );
  }

  return (
    <BulkActionButton
      destructive={action.destructive}
      escalatable={action.escalatable}
      cannotEscalate={action.cannotEscalate}
      disabled={action.disabled}
      onClick={() => void action.onSelect()}
    >
      {content}
    </BulkActionButton>
  );
}

export function BulkActionsBar({
  selectedCount,
  onClearSelection,
  children,
  actions,
  label,
  selectionScope,
  matchingCount,
  onSelectAllMatching,
  onSelectPageOnly,
  className,
}: BulkActionsBarProps) {
  const { t } = useTranslation('common');

  const escalated = selectionScope === 'matching';
  const scopeLabel = useBulkScopeLabel({ label, selectedCount, selectionScope, matchingCount });

  if (selectedCount === 0 && !escalated) return null;

  // The escalation is offered only when there is actually something more to
  // reach. On the last page (or an unfiltered short list) the page IS the set,
  // so the link would promise a change it cannot make.
  const canEscalate =
    !escalated &&
    selectionScope === 'page' &&
    onSelectAllMatching !== undefined &&
    matchingCount !== undefined &&
    matchingCount > selectedCount;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 sm:gap-4 px-3 sm:px-4 py-2 sm:py-3 bg-primary text-primary-foreground rounded-md mb-3',
        className
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium whitespace-nowrap">
          {scopeLabel}
        </span>
        {/* The scope switch sits with the COUNT, not with the actions: it changes
            what the selection is, it is not another thing to do to it. */}
        {canEscalate && (
          <Button
            size="sm"
            variant="link"
            onClick={onSelectAllMatching}
            className="text-primary-foreground underline px-1 h-auto py-1 touch:min-h-[44px]"
          >
            {t('app.selectAllMatching', { count: matchingCount })}
          </Button>
        )}
        {escalated && onSelectPageOnly && (
          <Button
            size="sm"
            variant="link"
            onClick={onSelectPageOnly}
            className="text-primary-foreground underline px-1 h-auto py-1 touch:min-h-[44px]"
          >
            {t('app.selectPageOnly')}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={onClearSelection}
          className="text-primary-foreground hover:bg-primary-foreground/20"
          aria-label={t('app.clearSelection')}
        >
          <X className="me-1 h-4 w-4" />
          {t('app.clear')}
        </Button>
      </div>
      <Separator orientation="vertical" className="h-6 bg-primary-foreground/30 hidden sm:block" />
      <div className="flex items-center gap-2 flex-wrap">
        <BulkSelectionScopeContext.Provider value={selectionScope ?? 'set'}>
          {actions?.map((action) => <DeclaredBulkAction key={action.id} action={action} />)}
          {children}
        </BulkSelectionScopeContext.Provider>
      </div>
    </div>
  );
}
